import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { FiFileText, FiDownload, FiEdit, FiCheck, FiX, FiArrowLeft, FiSave, FiTrash2, FiRefreshCw } from 'react-icons/fi';

const fmt = (n) => `Rs. ${Number(n || 0).toLocaleString('en-IN')}`;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const PayrollPage = () => {
  const { user } = useAuth();
  const isAdminHR = user.role === 'admin' || user.role === 'hr';
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [filterStatus, setFilterStatus] = useState('');
  const [generating, setGenerating] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  // Detail view
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  const fetchPayrolls = useCallback(async () => {
    try {
      setLoading(true);
      const params = { month, year };
      if (filterStatus) params.status = filterStatus;
      const res = await api.get('/payroll', { params });
      setPayrolls(res.data);
    } catch (err) { toast.error('Failed to fetch payroll'); }
    finally { setLoading(false); }
  }, [month, year, filterStatus]);

  useEffect(() => { fetchPayrolls(); }, [fetchPayrolls]);

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const res = await api.post('/payroll/generate', { month, year });
      toast.success(res.data.message);
      fetchPayrolls();
    } catch (err) { toast.error(err.response?.data?.message || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const downloadPayslip = async (id) => {
    try {
      const res = await api.get(`/payroll/${id}/payslip`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payslip-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) { toast.error('Download failed'); }
  };

  const handleMarkPaid = async (id) => {
    try {
      await api.put(`/payroll/${id}/pay`);
      toast.success('Marked as paid');
      fetchPayrolls();
      if (detail && detail._id === id) {
        setDetail({ ...detail, status: 'Paid', paidDate: new Date() });
      }
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleBulkPay = async () => {
    if (selectedIds.length === 0) return toast.error('Select records first');
    try {
      await api.put('/payroll/bulk-pay', { ids: selectedIds });
      toast.success(`${selectedIds.length} payrolls marked as paid`);
      setSelectedIds([]);
      fetchPayrolls();
    } catch (err) { toast.error('Bulk pay failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payroll record?')) return;
    try {
      await api.delete(`/payroll/${id}`);
      toast.success('Payroll deleted');
      if (detail && detail._id === id) setDetail(null);
      fetchPayrolls();
    } catch (err) { toast.error('Delete failed'); }
  };

  const openDetail = async (id) => {
    try {
      const res = await api.get(`/payroll/${id}`);
      setDetail(res.data);
      setEditing(false);
      setEditForm({
        basicSalary: res.data.basicSalary,
        hra: res.data.hra,
        da: res.data.da,
        ta: res.data.ta,
        deductions: res.data.deductions,
        tax: res.data.tax,
        unpaidLeaves: res.data.unpaidLeaves || 0,
        lop: res.data.lop || 0,
        variable: res.data.variable || 0,
        incentive: res.data.incentive || 0,
        leaveAdjustment: res.data.leaveAdjustment || 0,
      });
    } catch (err) { toast.error('Failed to load detail'); }
  };

  const handleSaveEdit = async () => {
    try {
      const res = await api.put(`/payroll/${detail._id}`, {
        basicSalary: Number(editForm.basicSalary),
        hra: Number(editForm.hra),
        da: Number(editForm.da),
        ta: Number(editForm.ta),
        deductions: Number(editForm.deductions),
        tax: Number(editForm.tax),
        unpaidLeaves: Number(editForm.unpaidLeaves),
        lop: Number(editForm.lop),
        variable: Number(editForm.variable),
        incentive: Number(editForm.incentive),
        leaveAdjustment: Number(editForm.leaveAdjustment),
      });
      setDetail({ ...detail, ...res.data });
      setEditing(false);
      toast.success('Payroll updated');
      fetchPayrolls();
    } catch (err) { toast.error('Update failed'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const pendingIds = payrolls.filter(p => p.status === 'Pending').map(p => p._id);
    setSelectedIds(prev => prev.length === pendingIds.length ? [] : pendingIds);
  };

  const pendingCount = payrolls.filter(p => p.status === 'Pending').length;
  const totalNet = payrolls.reduce((s, p) => s + (p.netSalary || 0), 0);
  const paidTotal = payrolls.filter(p => p.status === 'Paid').reduce((s, p) => s + (p.netSalary || 0), 0);

  // Detail View
  if (detail) {
    const gross = detail.basicSalary + detail.hra + detail.da + detail.ta;
    const lopAmount = detail.lop || 0;
    const totalDeductions = detail.deductions + detail.tax + lopAmount;
    return (
      <div>
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button className="btn-sm btn-secondary" onClick={() => setDetail(null)}><FiArrowLeft /></button>
            <h1>Payroll Detail</h1>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {isAdminHR && !editing && (
              <button className="btn-primary" onClick={() => setEditing(true)}><FiEdit /> Edit</button>
            )}
            {editing && (
              <button className="btn-primary" onClick={handleSaveEdit}><FiSave /> Save</button>
            )}
            {editing && (
              <button className="btn-secondary" onClick={() => setEditing(false)}><FiX /> Cancel</button>
            )}
            <button className="btn-sm btn-info" onClick={() => downloadPayslip(detail._id)}><FiDownload /> PDF</button>
            {isAdminHR && detail.status === 'Pending' && (
              <button className="btn-sm btn-success" onClick={() => handleMarkPaid(detail._id)}><FiCheck /> Mark Paid</button>
            )}
            {user.role === 'admin' && (
              <button className="btn-sm btn-danger" onClick={() => handleDelete(detail._id)}><FiTrash2 /></button>
            )}
          </div>
        </div>

        {/* Employee Info Card */}
        <div className="payroll-detail-grid">
          <div className="form-card">
            <h3 style={{ marginBottom: '1rem' }}>Employee Information</h3>
            <div className="detail-grid">
              <div className="detail-item"><label>Name</label><span>{detail.employee?.name}</span></div>
              <div className="detail-item"><label>Employee ID</label><span>{detail.employee?.employeeId}</span></div>
              <div className="detail-item"><label>Department</label><span>{detail.employee?.department?.name || 'N/A'}</span></div>
              <div className="detail-item"><label>Designation</label><span>{detail.employee?.designation}</span></div>
              <div className="detail-item"><label>Month</label><span>{MONTHS[detail.month - 1]} {detail.year}</span></div>
              <div className="detail-item">
                <label>Status</label>
                <span className={`status-badge status-${detail.status.toLowerCase()}`}>{detail.status}</span>
              </div>
            </div>
          </div>

          {/* Salary Breakdown */}
          <div className="form-card">
            <h3 style={{ marginBottom: '1rem' }}>Salary Breakdown</h3>

            <div className="payroll-breakdown">
              <div className="payroll-section">
                <h4 className="payroll-section-title earnings-title">Earnings</h4>
                <div className="payroll-row">
                  <span>Basic Salary</span>
                  {editing ? <input type="number" value={editForm.basicSalary} onChange={e => setEditForm({...editForm, basicSalary: e.target.value})} className="payroll-edit-input" />
                    : <span className="payroll-amount">{fmt(detail.basicSalary)}</span>}
                </div>
                <div className="payroll-row">
                  <span>HRA (House Rent Allowance)</span>
                  {editing ? <input type="number" value={editForm.hra} onChange={e => setEditForm({...editForm, hra: e.target.value})} className="payroll-edit-input" />
                    : <span className="payroll-amount">{fmt(detail.hra)}</span>}
                </div>
                <div className="payroll-row">
                  <span>DA (Dearness Allowance)</span>
                  {editing ? <input type="number" value={editForm.da} onChange={e => setEditForm({...editForm, da: e.target.value})} className="payroll-edit-input" />
                    : <span className="payroll-amount">{fmt(detail.da)}</span>}
                </div>
                <div className="payroll-row">
                  <span>TA (Travel Allowance)</span>
                  {editing ? <input type="number" value={editForm.ta} onChange={e => setEditForm({...editForm, ta: e.target.value})} className="payroll-edit-input" />
                    : <span className="payroll-amount">{fmt(detail.ta)}</span>}
                </div>
                <div className="payroll-row payroll-subtotal">
                  <span>Gross Salary</span>
                  <span className="payroll-amount">{fmt(editing ? (Number(editForm.basicSalary) + Number(editForm.hra) + Number(editForm.da) + Number(editForm.ta)) : gross)}</span>
                </div>
                <div className="payroll-row" style={{ marginTop: '0.5rem', borderTop: '1px dotted #e5e7eb', paddingTop: '0.5rem' }}>
                  <span>Variable Pay</span>
                  {editing ? <input type="number" value={editForm.variable} onChange={e => setEditForm({...editForm, variable: e.target.value})} className="payroll-edit-input" />
                    : <span className="payroll-amount">{fmt(detail.variable)}</span>}
                </div>
                <div className="payroll-row">
                  <span>Incentive</span>
                  {editing ? <input type="number" value={editForm.incentive} onChange={e => setEditForm({...editForm, incentive: e.target.value})} className="payroll-edit-input" />
                    : <span className="payroll-amount">{fmt(detail.incentive)}</span>}
                </div>
              </div>

              <div className="payroll-section">
                <h4 className="payroll-section-title deductions-title">Deductions</h4>
                <div className="payroll-row">
                  <span>PF (Provident Fund - 12%)</span>
                  {editing ? <input type="number" value={editForm.deductions} onChange={e => setEditForm({...editForm, deductions: e.target.value})} className="payroll-edit-input" />
                    : <span className="payroll-amount deduction">{fmt(detail.deductions)}</span>}
                </div>
                <div className="payroll-row">
                  <span>Tax (10%)</span>
                  {editing ? <input type="number" value={editForm.tax} onChange={e => setEditForm({...editForm, tax: e.target.value})} className="payroll-edit-input" />
                    : <span className="payroll-amount deduction">{fmt(detail.tax)}</span>}
                </div>
                {(detail.unpaidLeaves > 0 || editing) && (
                  <>
                    <div className="payroll-row" style={{ marginTop: '0.5rem', borderTop: '1px dotted #e5e7eb', paddingTop: '0.5rem' }}>
                      <span>Unpaid Leave Days</span>
                      {editing ? <input type="number" min="0" value={editForm.unpaidLeaves} onChange={e => setEditForm({...editForm, unpaidLeaves: e.target.value})} className="payroll-edit-input" />
                        : <span className="payroll-amount deduction" style={{ fontWeight: 700 }}>{detail.unpaidLeaves} day(s)</span>}
                    </div>
                    <div className="payroll-row">
                      <span>LOP (Loss of Pay)</span>
                      {editing ? <input type="number" min="0" value={editForm.lop} onChange={e => setEditForm({...editForm, lop: e.target.value})} className="payroll-edit-input" />
                        : <span className="payroll-amount deduction">{fmt(detail.lop)}</span>}
                    </div>
                  </>
                )}
                <div className="payroll-row payroll-subtotal">
                  <span>Total Deductions</span>
                  <span className="payroll-amount deduction">{fmt(editing ? (Number(editForm.deductions) + Number(editForm.tax) + Number(editForm.lop)) : totalDeductions)}</span>
                </div>
                <div className="payroll-row" style={{ marginTop: '0.5rem', borderTop: '1px dotted #e5e7eb', paddingTop: '0.5rem' }}>
                  <span>Leave Adjustment (+/-)</span>
                  {editing ? <input type="number" value={editForm.leaveAdjustment} onChange={e => setEditForm({...editForm, leaveAdjustment: e.target.value})} className="payroll-edit-input" />
                    : <span className="payroll-amount">{fmt(detail.leaveAdjustment)}</span>}
                </div>
              </div>

              <div className="payroll-net-row">
                <span>NET SALARY</span>
                <span className="payroll-net-amount">
                  {fmt(editing
                    ? (Number(editForm.basicSalary) + Number(editForm.hra) + Number(editForm.da) + Number(editForm.ta) - Number(editForm.deductions) - Number(editForm.tax) - Number(editForm.lop) + Number(editForm.variable) + Number(editForm.incentive) + Number(editForm.leaveAdjustment))
                    : detail.netSalary
                  )}
                </span>
              </div>
            </div>

            {detail.paidDate && (
              <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#6b7280' }}>
                Paid on: {new Date(detail.paidDate).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div>
      <div className="page-header">
        <h1><FiFileText style={{ marginRight: '0.5rem' }} /> Payroll</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn-secondary" onClick={fetchPayrolls} title="Refresh"><FiRefreshCw /></button>
          {isAdminHR && (
            <button className="btn-primary" onClick={handleGenerate} disabled={generating}>
              {generating ? 'Generating...' : 'Generate Payroll'}
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {isAdminHR && (
        <div className="summary-cards">
          <div className="summary-card">
            <div className="card-icon" style={{ color: '#4f46e5' }}>Rs.</div>
            <div><h3>{fmt(totalNet)}</h3><p>Total Payroll</p></div>
          </div>
          <div className="summary-card">
            <div className="card-icon" style={{ color: '#10b981' }}>Rs.</div>
            <div><h3>{fmt(paidTotal)}</h3><p>Paid</p></div>
          </div>
          <div className="summary-card">
            <div className="card-icon" style={{ color: '#f59e0b' }}>Rs.</div>
            <div><h3>{fmt(totalNet - paidTotal)}</h3><p>Pending</p></div>
          </div>
          <div className="summary-card">
            <div className="card-icon" style={{ color: '#6b7280' }}>#</div>
            <div><h3>{payrolls.length}</h3><p>Employees</p></div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-row">
        <select value={month} onChange={e => setMonth(Number(e.target.value))}>
          {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{MONTHS[i]}</option>)}
        </select>
        <select value={year} onChange={e => setYear(Number(e.target.value))}>
          {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        {isAdminHR && (
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Paid">Paid</option>
          </select>
        )}
        {isAdminHR && pendingCount > 0 && (
          <span className="pending-count-badge">{pendingCount} pending</span>
        )}
      </div>

      {/* Bulk Actions */}
      {isAdminHR && selectedIds.length > 0 && (
        <div className="bulk-actions-bar">
          <span>{selectedIds.length} selected</span>
          <button className="btn-sm btn-success" onClick={handleBulkPay}><FiCheck /> Mark All Paid</button>
        </div>
      )}

      {/* Payroll Table */}
      {loading ? <div className="loading">Loading...</div> : (
        <div className="form-card">
          <table className="table">
            <thead>
              <tr>
                {isAdminHR && <th style={{ width: 40 }}><input type="checkbox" onChange={toggleSelectAll} checked={selectedIds.length > 0 && selectedIds.length === payrolls.filter(p => p.status === 'Pending').length} /></th>}
                <th>Employee</th>
                <th>Basic</th>
                <th>HRA</th>
                <th>Deductions</th>
                <th>Unpaid Leave</th>
                <th>Net Salary</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payrolls.map(p => (
                <tr key={p._id}>
                  {isAdminHR && (
                    <td>{p.status === 'Pending' && <input type="checkbox" checked={selectedIds.includes(p._id)} onChange={() => toggleSelect(p._id)} />}</td>
                  )}
                  <td>
                    <div>{p.employee?.name || 'N/A'}</div>
                    <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>{p.employee?.employeeId}</div>
                  </td>
                  <td>{fmt(p.basicSalary)}</td>
                  <td>{fmt(p.hra)}</td>
                  <td style={{ color: '#ef4444' }}>{fmt(p.deductions + p.tax + (p.lop || 0))}</td>
                <td>
                  {p.unpaidLeaves > 0 ? (
                    <span style={{ color: '#ef4444', fontWeight: 600 }}>{p.unpaidLeaves} day(s)</span>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>0</span>
                  )}
                </td>
                  <td><strong>{fmt(p.netSalary)}</strong></td>
                  <td><span className={`status-badge status-${p.status.toLowerCase()}`}>{p.status}</span></td>
                  <td>
                    <div className="actions">
                      <button className="btn-sm btn-info" onClick={() => openDetail(p._id)} title="View Detail"><FiFileText /></button>
                      <button className="btn-sm btn-secondary" onClick={() => downloadPayslip(p._id)} title="Download PDF"><FiDownload /></button>
                      {isAdminHR && p.status === 'Pending' && (
                        <button className="btn-sm btn-success" onClick={() => handleMarkPaid(p._id)} title="Mark Paid"><FiCheck /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {payrolls.length === 0 && <tr><td colSpan={isAdminHR ? 9 : 8} style={{ textAlign: 'center' }}>No payroll records for {MONTHS[month - 1]} {year}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PayrollPage;
